import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Link as MuiLink,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  InputAdornment
} from '@mui/material';
import {
  Chat as ChatIcon,
  ContentCopy as CopyIcon,
  VerifiedUser as VerifiedUserIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  HelpOutline as HelpOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  MergeType as MergeTypeIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  PersonOff as PersonOffIcon
} from '@mui/icons-material';
import axios from 'axios';
import { axiosPostWithRetry } from '../utils/httpRetry';

interface CampaignOption {
  id: number;
  title: string;
}

interface DirectoryRow {
  id: number;
  full_name: string | null;
  instagram_username: string;
  instagram_url: string | null;
  follower_count: number;
  detected_story_count: number;
  campaigns_participated: number;
  /** null = henüz denenmedi, 1 = API OK, 0 = ulaşılamadı */
  profile_reachable?: number | null;
}

interface BatchVerifyFailRow {
  id: number;
  instagram_username: string;
  error: string;
}

interface BatchVerifyHintRow {
  id: number;
  stored_username: string;
  api_username: string;
}

type VerifyDialogState = {
  updated: { id: number; instagram_username: string }[];
  failed: BatchVerifyFailRow[];
  username_hints: BatchVerifyHintRow[];
  networkFailedIds: number[];
};

function mergeBatchResponseIntoVerifyState(
  prev: VerifyDialogState,
  data: {
    updated?: { id: number; instagram_username: string }[];
    failed?: BatchVerifyFailRow[];
    username_hints?: BatchVerifyHintRow[];
  }
): VerifyDialogState {
  let failed = [...prev.failed];
  let updated = [...prev.updated];
  let hints = [...prev.username_hints];
  for (const f of data.failed || []) {
    const idx = failed.findIndex((x) => x.id === f.id);
    if (idx >= 0) failed[idx] = f;
    else failed.push(f);
  }
  for (const u of data.updated || []) {
    failed = failed.filter((x) => x.id !== u.id);
    if (!updated.some((x) => x.id === u.id)) updated.push(u);
    hints = hints.filter((h) => h.id !== u.id);
  }
  for (const h of data.username_hints || []) {
    const idx = hints.findIndex((x) => x.id === h.id);
    if (idx >= 0) hints[idx] = h;
    else hints.push(h);
  }
  const networkFailedIds = prev.networkFailedIds.filter((nid) => !updated.some((u) => u.id === nid));
  return { ...prev, updated, failed, username_hints: hints, networkFailedIds };
}

/** Sunucu ile aynı mantık: küçük parçalar = kısa HTTP yanıtı (Cloudflare 524 önleme) */
const BATCH_VERIFY_CHUNK = 5;

type ProfileStatusFilter = 'all' | 'ok' | 'bad' | 'unknown';

type SortField =
  | 'full_name'
  | 'instagram_username'
  | 'follower_count'
  | 'detected_story_count'
  | 'campaigns_participated';

type SortOrder = 'asc' | 'desc';

/** Kopyalamada kullanılan kolonlar — etiketler ekrandaki başlıklarla aynı */
type CopyColumnKey =
  | 'no'
  | 'ad_soyad'
  | 'kullanici_adi'
  | 'takipci'
  | 'tespit_story'
  | 'kampanya_sayisi'
  | 'profil_baglantisi';

const COPY_COLUMNS: { key: CopyColumnKey; label: string }[] = [
  { key: 'no', label: 'Sıra' },
  { key: 'ad_soyad', label: 'Ad Soyad' },
  { key: 'kullanici_adi', label: 'Kullanıcı Adı' },
  { key: 'takipci', label: 'Takipçi Sayısı' },
  { key: 'tespit_story', label: 'Tespit Edilen Hikaye' },
  { key: 'kampanya_sayisi', label: 'Kampanya Sayısı' },
  { key: 'profil_baglantisi', label: 'Profil bağlantısı' }
];

/** TSV «Kopyala» açılışında yalnızca bunlar işaretli */
const DEFAULT_TSV_COPY_COLS: Record<CopyColumnKey, boolean> = {
  no: true,
  ad_soyad: true,
  kullanici_adi: false,
  takipci: true,
  tespit_story: false,
  kampanya_sayisi: false,
  profil_baglantisi: true
};

type WhatsAppColKey = 'no' | 'ad_soyad' | 'takipci' | 'profil_baglantisi';

const WHATSAPP_COLUMN_META: { key: WhatsAppColKey; label: string }[] = [
  { key: 'no', label: 'Sıra' },
  { key: 'ad_soyad', label: 'Ad Soyad' },
  { key: 'takipci', label: 'Takipçi Sayısı' },
  { key: 'profil_baglantisi', label: 'Profil bağlantısı' }
];

const DEFAULT_WHATSAPP_COLS: Record<WhatsAppColKey, boolean> = {
  no: true,
  ad_soyad: true,
  takipci: true,
  profil_baglantisi: true
};

/** Örnek: 1) *Ad Soyad*- 12.500\nhttps://... */
function buildWhatsAppText(rows: DirectoryRow[], opts: Record<WhatsAppColKey, boolean>): string {
  const blocks = rows.map((r, i) => {
    const displayName = (r.full_name && r.full_name.trim()) || r.instagram_username.replace(/^@/, '');
    const fc = r.follower_count > 0 ? r.follower_count.toLocaleString('tr-TR') : '-';
    const link = profileUrl(r);
    let line1 = '';
    if (opts.no) line1 += `${i + 1}) `;
    if (opts.ad_soyad && opts.takipci) {
      line1 += `*${displayName}*- ${fc}`;
    } else if (opts.ad_soyad && !opts.takipci) {
      line1 += `*${displayName}*`;
    } else if (!opts.ad_soyad && opts.takipci) {
      line1 += fc;
    }
    const line1Trim = line1.trim();
    if (opts.profil_baglantisi) {
      if (line1Trim) return `${line1Trim}\n${link}`;
      return link;
    }
    return line1Trim;
  });
  return blocks.filter(Boolean).join('\n\n');
}

function num(v: unknown): number {
  if (typeof v === 'bigint') return Number(v);
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function profileUrl(row: DirectoryRow): string {
  const u = (row.instagram_url || '').trim();
  if (u) return u;
  const name = String(row.instagram_username || '').replace(/^@/, '');
  return `https://www.instagram.com/${name}/`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && typeof window !== 'undefined' && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    throw new Error('Clipboard API not available');
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textArea);
    }
  }
}

function buildParams(
  page: number,
  rowsPerPage: number,
  debouncedSearch: string,
  campaignIdsParam: number[],
  followerMin: string,
  followerMax: string,
  minStoryCount: string,
  sortField: SortField,
  sortOrder: SortOrder,
  profileStatus: ProfileStatusFilter,
  allResults?: boolean
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    page: page + 1,
    limit: rowsPerPage,
    sort_by: sortField,
    sort_order: sortOrder
  };
  if (allResults) {
    params.all_results = true;
    params.page = 1;
    params.limit = 50000;
  }
  if (debouncedSearch) params.search = debouncedSearch;
  if (campaignIdsParam.length) params.campaign_ids = campaignIdsParam.join(',');
  const minN = parseFloat(String(followerMin).trim().replace(',', '.'));
  if (followerMin !== '' && Number.isFinite(minN)) params.follower_min = minN;
  const maxN = parseFloat(String(followerMax).trim().replace(',', '.'));
  if (followerMax !== '' && Number.isFinite(maxN)) params.follower_max = maxN;
  const ms = parseInt(String(minStoryCount).trim(), 10);
  if (minStoryCount !== '' && Number.isFinite(ms) && ms >= 0) params.min_story_count = ms;
  if (profileStatus !== 'all') params.profile_status = profileStatus;
  return params;
}

function mapRows(raw: unknown[]): DirectoryRow[] {
  return raw.map((r) => {
    const row = r as Record<string, unknown>;
    const pr = row.profile_reachable;
    return {
      id: num(row.id),
      full_name: row.full_name != null && String(row.full_name).trim() !== '' ? String(row.full_name) : null,
      instagram_username: String(row.instagram_username || ''),
      instagram_url: row.instagram_url != null ? String(row.instagram_url) : null,
      follower_count: num(row.follower_count),
      detected_story_count: num(row.detected_story_count),
      campaigns_participated: num(row.campaigns_participated),
      profile_reachable: pr === null || pr === undefined ? null : num(pr)
    };
  });
}

function buildCopyTsv(
  rows: DirectoryRow[],
  activeKeys: CopyColumnKey[],
  labelByKey: Map<CopyColumnKey, string>
): string {
  const labels = activeKeys.map((k) => labelByKey.get(k) || k);
  const lines: string[] = [labels.join('\t')];
  rows.forEach((r, idx) => {
    const cells: string[] = [];
    for (const k of activeKeys) {
      switch (k) {
        case 'no':
          cells.push(String(idx + 1));
          break;
        case 'ad_soyad':
          cells.push((r.full_name || '').replace(/\t/g, ' '));
          break;
        case 'kullanici_adi':
          cells.push(r.instagram_username.replace(/^@/, '').replace(/\t/g, ' '));
          break;
        case 'takipci':
          cells.push(String(r.follower_count));
          break;
        case 'tespit_story':
          cells.push(String(r.detected_story_count));
          break;
        case 'kampanya_sayisi':
          cells.push(String(r.campaigns_participated));
          break;
        case 'profil_baglantisi':
          cells.push(profileUrl(r));
          break;
        default:
          cells.push('');
      }
    }
    lines.push(cells.join('\t'));
  });
  return lines.join('\n');
}

const InfluencerDirectory: React.FC = () => {
  const labelByKey = useMemo(() => new Map(COPY_COLUMNS.map((c) => [c.key, c.label])), []);

  const [campaignOptions, setCampaignOptions] = useState<CampaignOption[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<CampaignOption[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [followerMin, setFollowerMin] = useState<string>('');
  const [followerMax, setFollowerMax] = useState<string>('');
  const [minStoryCount, setMinStoryCount] = useState<string>('');
  const [profileStatus, setProfileStatus] = useState<ProfileStatusFilter>('all');

  const [sortField, setSortField] = useState<SortField>('detected_story_count');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const [rows, setRows] = useState<DirectoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [bulkSelectLoading, setBulkSelectLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const [selectedById, setSelectedById] = useState<Map<number, DirectoryRow>>(() => new Map());

  const [copyOpen, setCopyOpen] = useState(false);
  const [copyCols, setCopyCols] = useState<Record<CopyColumnKey, boolean>>(() => ({ ...DEFAULT_TSV_COPY_COLS }));

  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [whatsappCols, setWhatsappCols] = useState<Record<WhatsAppColKey, boolean>>(() => ({
    ...DEFAULT_WHATSAPP_COLS
  }));

  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);
  const [verifyRunning, setVerifyRunning] = useState(false);
  const [verifyProgress, setVerifyProgress] = useState({ done: 0, total: 0 });
  const [verifyResultOpen, setVerifyResultOpen] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyDialogState | null>(null);

  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSourceUsername, setMergeSourceUsername] = useState('');
  const [mergeTargetUsername, setMergeTargetUsername] = useState('');
  const [mergeSubmitting, setMergeSubmitting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  const [usernameEditOpen, setUsernameEditOpen] = useState(false);
  const [usernameEdit, setUsernameEdit] = useState<{ id: number } | null>(null);
  const [usernameEditValue, setUsernameEditValue] = useState('');
  const [usernameEditSaving, setUsernameEditSaving] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/api/campaigns', { params: { page: 1, limit: 500 } });
        const list = data?.campaigns || [];
        if (!cancelled) {
          setCampaignOptions(
            list.map((c: { id: number; title: string }) => ({
              id: Number(c.id),
              title: String(c.title || '')
            }))
          );
        }
      } catch {
        if (!cancelled) setCampaignOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const campaignIdsParam = useMemo(
    () => selectedCampaigns.map((c) => c.id).sort((a, b) => a - b),
    [selectedCampaigns]
  );

  const fetchDirectory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = buildParams(
        page,
        rowsPerPage,
        debouncedSearch,
        campaignIdsParam,
        followerMin,
        followerMax,
        minStoryCount,
        sortField,
        sortOrder,
        profileStatus
      );
      const { data } = await axios.get('/api/influencers/directory', { params });
      const mapped = mapRows(data?.influencers || []);
      setRows(mapped);
      setTotal(num(data?.pagination?.total));

      setSelectedById((prev) => {
        const next = new Map(prev);
        for (const r of mapped) {
          if (next.has(r.id)) next.set(r.id, r);
        }
        return next;
      });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Liste yüklenemedi');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    campaignIdsParam,
    followerMin,
    followerMax,
    minStoryCount,
    sortField,
    sortOrder,
    profileStatus
  ]);

  useEffect(() => {
    void fetchDirectory();
  }, [fetchDirectory]);

  const campaignIdsKey = campaignIdsParam.join(',');

  useLayoutEffect(() => {
    setPage(0);
  }, [debouncedSearch, campaignIdsKey, followerMin, followerMax, minStoryCount, profileStatus]);

  useLayoutEffect(() => {
    setPage(0);
  }, [sortField, sortOrder]);

  useEffect(() => {
    setSelectedById(new Map());
  }, [debouncedSearch, campaignIdsKey, followerMin, followerMax, minStoryCount, profileStatus]);

  const showSnack = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnack({ open: true, message, severity });
  };

  const runBatchVerifyCore = async (ids: number[]): Promise<VerifyDialogState | null> => {
    const acc: VerifyDialogState = {
      updated: [],
      failed: [],
      username_hints: [],
      networkFailedIds: []
    };
    if (!navigator.onLine) {
      showSnack('İnternet bağlantısı yok. Bağlantıyı kontrol edin.', 'error');
      return null;
    }
    for (let i = 0; i < ids.length; i += BATCH_VERIFY_CHUNK) {
      if (i > 0) {
        await new Promise((r) => setTimeout(r, 200));
      }
      if (!navigator.onLine) {
        acc.networkFailedIds.push(...ids.slice(i));
        setVerifyProgress({ done: ids.length, total: ids.length });
        break;
      }
      const chunk = ids.slice(i, i + BATCH_VERIFY_CHUNK);
      try {
        const { data } = await axiosPostWithRetry<{
          updated: { id: number; instagram_username: string }[];
          failed: BatchVerifyFailRow[];
          username_hints: BatchVerifyHintRow[];
        }>('/api/influencers/batch-verify-profiles', { influencer_ids: chunk }, { retries: 3, baseDelayMs: 1000 });
        acc.updated.push(...(data.updated || []));
        acc.failed.push(...(data.failed || []));
        acc.username_hints.push(...(data.username_hints || []));
      } catch {
        acc.networkFailedIds.push(...chunk);
      }
      setVerifyProgress({ done: Math.min(i + chunk.length, ids.length), total: ids.length });
    }
    return acc;
  };

  const runBatchVerify = async () => {
    const ids = Array.from(selectedById.keys());
    if (!ids.length) return;
    setVerifyConfirmOpen(false);
    setVerifyRunning(true);
    setVerifyProgress({ done: 0, total: ids.length });
    try {
      const acc = await runBatchVerifyCore(ids);
      if (!acc) return;
      setVerifyResult(acc);
      setVerifyResultOpen(true);
      const sev =
        acc.networkFailedIds.length > 0 ? 'warning' : acc.failed.length > 0 ? 'warning' : 'success';
      showSnack(
        `${acc.updated.length} profil güncellendi; ${acc.failed.length} API sorunu.${
          acc.networkFailedIds.length ? ` ${acc.networkFailedIds.length} kayıt ağ hatası (tekrar deneyin).` : ''
        }`,
        sev
      );
      void fetchDirectory();
    } finally {
      setVerifyRunning(false);
      setVerifyProgress({ done: 0, total: 0 });
    }
  };

  const retryNetworkFailedVerify = async () => {
    const pend = verifyResult?.networkFailedIds;
    if (!pend?.length) return;
    setVerifyRunning(true);
    setVerifyProgress({ done: 0, total: pend.length });
    try {
      const acc2 = await runBatchVerifyCore(pend);
      if (!acc2 || !verifyResult) return;
      const updatedMap = new Map<number, { id: number; instagram_username: string }>();
      for (const u of [...verifyResult.updated, ...acc2.updated]) {
        updatedMap.set(u.id, u);
      }
      let failed = verifyResult.failed.filter((f) => !acc2.updated.some((u) => u.id === f.id));
      for (const f of acc2.failed) {
        const idx = failed.findIndex((x) => x.id === f.id);
        if (idx >= 0) failed[idx] = f;
        else failed.push(f);
      }
      let hints = verifyResult.username_hints.filter((h) => !acc2.updated.some((u) => u.id === h.id));
      for (const h of acc2.username_hints) {
        const idx = hints.findIndex((x) => x.id === h.id);
        if (idx >= 0) hints[idx] = h;
        else hints.push(h);
      }
      setVerifyResult({
        updated: Array.from(updatedMap.values()),
        failed,
        username_hints: hints,
        networkFailedIds: acc2.networkFailedIds
      });
      showSnack(
        acc2.networkFailedIds.length
          ? `Hâlâ ${acc2.networkFailedIds.length} kayıtta ağ hatası.`
          : 'Ağ hatası olan parçalar yeniden denendi.',
        acc2.networkFailedIds.length ? 'warning' : 'success'
      );
      void fetchDirectory();
    } finally {
      setVerifyRunning(false);
      setVerifyProgress({ done: 0, total: 0 });
    }
  };

  const handleRemoveProfile = async (row: DirectoryRow) => {
    const uname = String(row.instagram_username || '').replace(/^@/, '').trim();
    if (
      !window.confirm(
        `@${uname || row.id}: Profil kaydı silinecek. Kampanyalardaki tespitler ve görev kotaları korunur (kullanıcı adı arşivlenir). Geri alınamaz. Devam edilsin mi?`
      )
    ) {
      return;
    }
    try {
      await axios.delete(`/api/influencers/${row.id}`);
      setSelectedById((prev) => {
        const next = new Map(prev);
        next.delete(row.id);
        return next;
      });
      showSnack('Profil silindi; kampanya verileri korundu.', 'success');
      void fetchDirectory();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showSnack(err.response?.data?.message || 'Silinemedi', 'error');
    }
  };

  const handleDeleteInfluencerFromVerify = async (id: number) => {
    if (
      !window.confirm(
        'Bu influencer profil kaydını silmek üzeresiniz. Kampanyalardaki tespitler korunur; yalnızca dizindeki profil kalkar. Devam edilsin mi?'
      )
    ) {
      return;
    }
    try {
      await axios.delete(`/api/influencers/${id}`);
      setVerifyResult((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          failed: prev.failed.filter((f) => f.id !== id),
          username_hints: prev.username_hints.filter((h) => h.id !== id),
          updated: prev.updated.filter((u) => u.id !== id),
          networkFailedIds: prev.networkFailedIds.filter((nid) => nid !== id)
        };
      });
      setSelectedById((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      showSnack('Profil silindi; kampanya verileri korundu.', 'success');
      void fetchDirectory();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showSnack(err.response?.data?.message || 'Silinemedi', 'error');
    }
  };

  const openUsernameEdit = (id: number, currentUsername: string) => {
    setUsernameEdit({ id });
    setUsernameEditValue(String(currentUsername || '').replace(/^@/, '').trim());
    setUsernameEditOpen(true);
  };

  const saveUsernameEdit = async () => {
    if (!usernameEdit) return;
    const u = usernameEditValue.trim().replace(/^@/, '');
    if (!/^[a-zA-Z0-9._]+$/.test(u)) {
      showSnack('Geçersiz Instagram kullanıcı adı formatı', 'error');
      return;
    }
    setUsernameEditSaving(true);
    try {
      await axios.put(`/api/influencers/${usernameEdit.id}`, { instagram_username: u });
      setUsernameEditOpen(false);
      const savedId = usernameEdit.id;
      setUsernameEdit(null);
      setVerifyResult((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          failed: prev.failed.filter((f) => f.id !== savedId),
          username_hints: prev.username_hints.filter((h) => h.id !== savedId),
          networkFailedIds: prev.networkFailedIds.filter((nid) => nid !== savedId)
        };
      });
      try {
        const { data } = await axiosPostWithRetry<{
          updated: { id: number; instagram_username: string }[];
          failed: BatchVerifyFailRow[];
          username_hints: BatchVerifyHintRow[];
        }>(
          '/api/influencers/batch-verify-profiles',
          { influencer_ids: [savedId] },
          { retries: 3, baseDelayMs: 1000 }
        );
        const ok = (data.updated || []).some((x) => x.id === savedId);
        showSnack(
          ok ? 'Kullanıcı adı güncellendi ve profil doğrulandı.' : 'Kullanıcı adı kaydedildi; API hâlâ sorunlu.',
          ok ? 'success' : 'warning'
        );
        setVerifyResult((prev) => (prev ? mergeBatchResponseIntoVerifyState(prev, data) : prev));
      } catch {
        showSnack('Kullanıcı adı kaydedildi; doğrulama isteği başarısız. Sonra «Profilleri doğrula» deneyin.', 'warning');
      }
      void fetchDirectory();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showSnack(err.response?.data?.message || 'Güncellenemedi', 'error');
    } finally {
      setUsernameEditSaving(false);
    }
  };

  const applySuggestedUsername = async (id: number, apiUsername: string) => {
    const u = String(apiUsername || '').trim().replace(/^@/, '');
    if (!u || !/^[a-zA-Z0-9._]+$/.test(u)) {
      showSnack('Geçersiz önerilen kullanıcı adı', 'error');
      return;
    }
    if (
      !window.confirm(
        `Veritabanındaki kullanıcı adı «${u}» olarak güncellenecek (API yanıtı). Onaylıyor musunuz?`
      )
    ) {
      return;
    }
    setUsernameEditSaving(true);
    try {
      await axios.put(`/api/influencers/${id}`, { instagram_username: u });
      setVerifyResult((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          failed: prev.failed.filter((f) => f.id !== id),
          username_hints: prev.username_hints.filter((h) => h.id !== id),
          networkFailedIds: prev.networkFailedIds.filter((nid) => nid !== id)
        };
      });
      try {
        const { data } = await axiosPostWithRetry<{
          updated: { id: number; instagram_username: string }[];
          failed: BatchVerifyFailRow[];
          username_hints: BatchVerifyHintRow[];
        }>(
          '/api/influencers/batch-verify-profiles',
          { influencer_ids: [id] },
          { retries: 3, baseDelayMs: 1000 }
        );
        const ok = (data.updated || []).some((x) => x.id === id);
        showSnack(
          ok ? 'Önerilen kullanıcı adı kaydedildi ve profil doğrulandı.' : 'Kaydedildi; API hâlâ sorunlu.',
          ok ? 'success' : 'warning'
        );
        setVerifyResult((prev) => (prev ? mergeBatchResponseIntoVerifyState(prev, data) : prev));
      } catch {
        showSnack('Kaydedildi; doğrulama isteği başarısız. Sonra «Profilleri doğrula» deneyin.', 'warning');
      }
      void fetchDirectory();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showSnack(err.response?.data?.message || 'Güncellenemedi', 'error');
    } finally {
      setUsernameEditSaving(false);
    }
  };

  const retryVerifySingle = async (id: number) => {
    try {
      const { data } = await axiosPostWithRetry<{
        updated: { id: number; instagram_username: string }[];
        failed: BatchVerifyFailRow[];
        username_hints: BatchVerifyHintRow[];
      }>('/api/influencers/batch-verify-profiles', { influencer_ids: [id] }, { retries: 3, baseDelayMs: 1000 });
      setVerifyResult((prev) =>
        prev ? mergeBatchResponseIntoVerifyState(prev, data) : prev
      );
      void fetchDirectory();
      const ok = (data.updated || []).some((x: { id: number }) => x.id === id);
      showSnack(ok ? 'Profil güncellendi.' : 'Hâlâ sorunlu.', ok ? 'success' : 'warning');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showSnack(err.response?.data?.message || 'Tekrar deneme başarısız', 'error');
    }
  };

  const normalizeMergeUsernameInput = (raw: string) =>
    raw.trim().replace(/^@+/, '').trim();

  const submitMerge = async () => {
    const sourceU = normalizeMergeUsernameInput(mergeSourceUsername);
    const targetU = normalizeMergeUsernameInput(mergeTargetUsername);
    if (!sourceU || !targetU) {
      showSnack('Kaynak ve hedef Instagram kullanıcı adını girin (@ olmadan da yazabilirsiniz).', 'error');
      return;
    }
    if (sourceU.toLowerCase() === targetU.toLowerCase()) {
      showSnack('Kaynak ve hedef farklı olmalı.', 'error');
      return;
    }
    if (
      !window.confirm(
        `Kaynak @${sourceU} silinecek; kampanya atamaları ile story ve gönderi tespitleri @${targetU} kaydında birleştirilecek. Geri alınamaz. Onaylıyor musunuz?`
      )
    ) {
      return;
    }
    setMergeSubmitting(true);
    try {
      const { data } = await axios.post<{
        kept_id?: number;
        removed_id?: number;
        kept_username?: string;
        removed_username?: string;
      }>('/api/influencers/merge', { source_username: sourceU, target_username: targetU });
      const kept = String(data?.kept_username ?? targetU).replace(/^@/, '');
      const removed = String(data?.removed_username ?? sourceU).replace(/^@/, '');
      const kid = data?.kept_id;
      const rid = data?.removed_id;
      showSnack(
        kid != null && rid != null
          ? `Birleştirildi: @${removed} kaldırıldı, kayıt @${kept} altında (id ${kid}).`
          : 'Kayıtlar birleştirildi.',
        'success'
      );
      setMergeOpen(false);
      setMergeSourceUsername('');
      setMergeTargetUsername('');
      setSelectedById((prev) => {
        const next = new Map(prev);
        if (rid != null) next.delete(rid);
        return next;
      });
      void fetchDirectory();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showSnack(err.response?.data?.message || 'Birleştirme başarısız', 'error');
    } finally {
      setMergeSubmitting(false);
    }
  };

  const pageRowIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allOnPageSelected =
    rows.length > 0 && pageRowIds.every((id) => selectedById.has(id));
  const someOnPageSelected = pageRowIds.some((id) => selectedById.has(id));

  const selectedCount = selectedById.size;
  const allResultsSelected = total > 0 && selectedCount === total;

  const toggleRow = (row: DirectoryRow) => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) {
        for (const id of pageRowIds) next.delete(id);
      } else {
        for (const r of rows) next.set(r.id, r);
      }
      return next;
    });
  };

  const selectedRowsList = useMemo(() => Array.from(selectedById.values()), [selectedById]);

  const fetchAllMatchingRows = async (): Promise<DirectoryRow[]> => {
    const params = buildParams(
      0,
      25,
      debouncedSearch,
      campaignIdsParam,
      followerMin,
      followerMax,
      minStoryCount,
      sortField,
      sortOrder,
      profileStatus,
      true
    );
    const { data } = await axios.get('/api/influencers/directory', { params });
    return mapRows(data?.influencers || []);
  };

  const handleSelectAllResults = async () => {
    if (!total) {
      showSnack('Seçilecek kayıt yok.', 'info');
      return;
    }
    setBulkSelectLoading(true);
    try {
      const all = await fetchAllMatchingRows();
      const next = new Map<number, DirectoryRow>();
      for (const r of all) next.set(r.id, r);
      setSelectedById(next);
      showSnack(`${all.length} kişi seçildi (filtreyle eşleşen tüm sonuçlar).`, 'success');
    } catch {
      showSnack('Liste alınamadı.', 'error');
    } finally {
      setBulkSelectLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedById(new Map());
    showSnack('Seçim temizlendi.', 'info');
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const openCopyDialog = () => {
    if (selectedCount === 0) {
      showSnack('Önce satır seçin veya «Tüm sonuçları seç» kullanın.', 'info');
      return;
    }
    setCopyCols({ ...DEFAULT_TSV_COPY_COLS });
    setCopyOpen(true);
  };

  const openWhatsappDialog = () => {
    if (selectedCount === 0) {
      showSnack('Önce satır seçin veya «Tüm sonuçları seç» kullanın.', 'info');
      return;
    }
    setWhatsappCols({ ...DEFAULT_WHATSAPP_COLS });
    setWhatsappOpen(true);
  };

  const handleConfirmWhatsappCopy = async () => {
    const anyCol = WHATSAPP_COLUMN_META.some((c) => whatsappCols[c.key]);
    if (!anyCol) {
      showSnack('En az bir alan seçin.', 'info');
      return;
    }
    const text = buildWhatsAppText(selectedRowsList, whatsappCols);
    if (!text.trim()) {
      showSnack('Seçimlerle metin oluşmadı.', 'info');
      return;
    }
    const ok = await copyToClipboard(text);
    if (ok) {
      setWhatsappOpen(false);
      showSnack('WhatsApp metni panoya kopyalandı.', 'success');
    } else {
      showSnack('Kopyalama başarısız.', 'error');
    }
  };

  const handleConfirmCopy = async () => {
    const activeKeys = COPY_COLUMNS.map((c) => c.key).filter((k) => copyCols[k]);
    if (activeKeys.length === 0) {
      showSnack('En az bir kolon seçin.', 'info');
      return;
    }
    const text = buildCopyTsv(selectedRowsList, activeKeys, labelByKey);
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopyOpen(false);
      showSnack(`${selectedRowsList.length} satır panoya kopyalandı.`, 'success');
    } else {
      showSnack('Kopyalama başarısız.', 'error');
    }
  };

  const followerInputProps = { step: 'any', inputMode: 'decimal' as const };

  return (
    <Box p={{ xs: 2, md: 3 }} maxWidth={1600} mx="auto">
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Influencerlar
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Kampanyaya atanmış influencer’lar. Hikaye sütunu, tüm kampanyalarda tespit edilen story toplamını gösterir.
        </Typography>
      </Box>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
              <Autocomplete
                multiple
                sx={{ flex: 1, minWidth: 240 }}
                options={campaignOptions}
                getOptionLabel={(o) => o.title}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={selectedCampaigns}
                onChange={(_, v) => setSelectedCampaigns(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Kampanyalar" placeholder="Tümü (boş bırakın)" />
                )}
              />
              <TextField
                sx={{ flex: 1, minWidth: 240 }}
                label="Influencer ara"
                placeholder="Ad, kullanıcı adı veya bio"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
              <TextField
                label="Min. takipçi"
                type="number"
                value={followerMin}
                onChange={(e) => setFollowerMin(e.target.value)}
                inputProps={followerInputProps}
                sx={{ minWidth: 140 }}
              />
              <TextField
                label="Maks. takipçi"
                type="number"
                value={followerMax}
                onChange={(e) => setFollowerMax(e.target.value)}
                inputProps={followerInputProps}
                sx={{ minWidth: 140 }}
              />
              <TextField
                label="Min. hikaye paylaşımı"
                type="number"
                value={minStoryCount}
                onChange={(e) => setMinStoryCount(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                sx={{ minWidth: 180 }}
                helperText="Tüm kampanyalarda tespit edilen story toplamı ≥ bu sayı"
              />
              <TextField
                select
                label="Profil API durumu"
                value={profileStatus}
                onChange={(e) => setProfileStatus(e.target.value as ProfileStatusFilter)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="ok">API OK</MenuItem>
                <MenuItem value="bad">API sorunlu</MenuItem>
                <MenuItem value="unknown">Henüz denenmedi</MenuItem>
              </TextField>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Çift kayıtları birleştirirken kaynak ve hedef Instagram kullanıcı adlarını girin; kampanya ve tespitler hedef
              hesapta toplanır.
            </Typography>
            <Stack
              direction="row"
              flexWrap="wrap"
              spacing={1}
              useFlexGap
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!total || bulkSelectLoading}
                  onClick={() => void handleSelectAllResults()}
                >
                  {bulkSelectLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                  Tüm sonuçları seç
                </Button>
                <Button size="small" variant="text" disabled={!selectedCount} onClick={handleClearSelection}>
                  Seçimi temizle
                </Button>
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    disabled={!selectedCount}
                    onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  >
                    Dışa aktar
                  </Button>
                  <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={() => setExportMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  >
                    <MenuItem
                      disabled={!selectedCount}
                      onClick={() => {
                        setExportMenuAnchor(null);
                        openCopyDialog();
                      }}
                    >
                      Panoya kopyala (TSV)
                    </MenuItem>
                    <MenuItem
                      disabled={!selectedCount}
                      onClick={() => {
                        setExportMenuAnchor(null);
                        openWhatsappDialog();
                      }}
                    >
                      WhatsApp için kopyala
                    </MenuItem>
                  </Menu>
                </>
              </Stack>
              <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap alignItems="center">
                <Button
                  size="medium"
                  variant="outlined"
                  color="secondary"
                  startIcon={
                    verifyRunning ? <CircularProgress color="inherit" size={18} sx={{ mr: 0.5 }} /> : <VerifiedUserIcon />
                  }
                  disabled={!selectedCount || verifyRunning}
                  onClick={() => setVerifyConfirmOpen(true)}
                >
                  Profilleri doğrula…
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<MergeTypeIcon />}
                  onClick={() => {
                    setMergeSourceUsername('');
                    setMergeTargetUsername('');
                    setMergeOpen(true);
                  }}
                >
                  Kayıtları birleştir…
                </Button>
              </Stack>
            </Stack>
            {verifyRunning && verifyProgress.total > 0 ? (
              <Box sx={{ width: '100%', mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (verifyProgress.done / verifyProgress.total) * 100)}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {verifyProgress.done} / {verifyProgress.total} profil işlendi
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2, alignItems: 'center' }}
      >
        <Chip label={`Toplam sonuç: ${total.toLocaleString('tr-TR')}`} variant="outlined" size="small" />
        <Chip
          label={`Seçili: ${selectedCount.toLocaleString('tr-TR')}`}
          color={selectedCount > 0 ? 'primary' : 'default'}
          variant={selectedCount > 0 ? 'filled' : 'outlined'}
          size="small"
        />
        {total > 0 && allResultsSelected && (
          <Chip label="Tüm sonuçlar seçili" color="success" size="small" variant="outlined" />
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Kampanya detayı «Influencer Başına Performans» tablosu ile aynı Paper/typography/chip dili */}
      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 48 }}>
                <Checkbox
                  size="small"
                  indeterminate={someOnPageSelected && !allOnPageSelected}
                  checked={allOnPageSelected}
                  onChange={() => toggleSelectAllPage()}
                  inputProps={{ 'aria-label': 'Bu sayfadakilerin tümünü seç' }}
                />
              </TableCell>
              <TableCell>Sıra</TableCell>
              <TableCell sortDirection={sortField === 'full_name' ? sortOrder : false}>
                <TableSortLabel
                  active={sortField === 'full_name'}
                  direction={sortField === 'full_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('full_name')}
                >
                  Ad Soyad
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortField === 'instagram_username' ? sortOrder : false}>
                <TableSortLabel
                  active={sortField === 'instagram_username'}
                  direction={sortField === 'instagram_username' ? sortOrder : 'asc'}
                  onClick={() => handleSort('instagram_username')}
                >
                  Kullanıcı Adı
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={sortField === 'follower_count' ? sortOrder : false}>
                <TableSortLabel
                  active={sortField === 'follower_count'}
                  direction={sortField === 'follower_count' ? sortOrder : 'asc'}
                  onClick={() => handleSort('follower_count')}
                  sx={{ justifyContent: 'flex-end' }}
                >
                  Takipçi Sayısı
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sortDirection={sortField === 'detected_story_count' ? sortOrder : false}>
                <TableSortLabel
                  active={sortField === 'detected_story_count'}
                  direction={sortField === 'detected_story_count' ? sortOrder : 'asc'}
                  onClick={() => handleSort('detected_story_count')}
                  sx={{ justifyContent: 'center' }}
                >
                  Tespit Edilen Hikaye
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sortDirection={sortField === 'campaigns_participated' ? sortOrder : false}>
                <TableSortLabel
                  active={sortField === 'campaigns_participated'}
                  direction={sortField === 'campaigns_participated' ? sortOrder : 'asc'}
                  onClick={() => handleSort('campaigns_participated')}
                  sx={{ justifyContent: 'center' }}
                >
                  Kampanya Sayısı
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ width: 56 }}>
                Profil API
              </TableCell>
              <TableCell align="center" sx={{ width: 52, px: 0.5 }}>
                İşlem
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Kayıt yok
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r, idx) => {
                const rowNo = page * rowsPerPage + idx + 1;
                const href = profileUrl(r);
                const uname = r.instagram_username.replace(/^@/, '');
                return (
                  <TableRow key={r.id} selected={selectedById.has(r.id)} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedById.has(r.id)}
                        onChange={() => toggleRow(r)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{rowNo}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.full_name || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <MuiLink href={href} target="_blank" rel="noopener noreferrer" underline="hover">
                        <Typography variant="body2" color="primary">
                          @{uname}
                        </Typography>
                      </MuiLink>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {r.follower_count > 0 ? r.follower_count.toLocaleString('tr-TR') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={r.detected_story_count}
                        color={r.detected_story_count > 0 ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={r.campaigns_participated}
                        color={r.campaigns_participated > 0 ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const pr = r.profile_reachable;
                        if (pr === 1) {
                          return (
                            <Tooltip title="Son kontrol: profil erişilebilir">
                              <CheckCircleOutlineIcon fontSize="small" color="success" />
                            </Tooltip>
                          );
                        }
                        if (pr === 0) {
                          return (
                            <Tooltip title="Son kontrol: API yanıt vermedi veya profil yok">
                              <ErrorOutlineIcon fontSize="small" color="error" />
                            </Tooltip>
                          );
                        }
                        return (
                          <Tooltip title="Henüz toplu doğrulama yapılmadı">
                            <HelpOutlineIcon fontSize="small" color="action" />
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                      <Box sx={{ display: 'inline-flex', gap: 0.25, justifyContent: 'center' }}>
                        <Tooltip title="Kullanıcı adını düzenle">
                          <IconButton
                            size="small"
                            aria-label="Kullanıcı adını düzenle"
                            onClick={() => openUsernameEdit(r.id, r.instagram_username)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Profil sil — kampanya tespitleri kalır">
                          <IconButton
                            size="small"
                            color="warning"
                            aria-label="Profil sil"
                            onClick={() => void handleRemoveProfile(r)}
                          >
                            <PersonOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Sayfa başına:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `${to}’den fazla`}`}
        />
      </TableContainer>

      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Panoya kopyala (TSV)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {selectedCount} kişi kopyalanacak. Başlıklar tablodaki adlarla aynıdır (sekme ile ayrılmış).
          </Typography>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            Kolonlar
          </Typography>
          <FormGroup>
            {COPY_COLUMNS.map((c) => (
              <FormControlLabel
                key={c.key}
                control={
                  <Checkbox
                    checked={copyCols[c.key]}
                    onChange={(e) => setCopyCols((prev) => ({ ...prev, [c.key]: e.target.checked }))}
                  />
                }
                label={c.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCopyOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={() => void handleConfirmCopy()} startIcon={<CopyIcon />}>
            Kopyala
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={whatsappOpen} onClose={() => setWhatsappOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>WhatsApp için kopyala</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {selectedCount} kişi; her biri için şablon: sıra + kalın ad + takipçi, alt satırda profil linki.
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover', typography: 'caption', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {`1) *Ad Soyad*- 12.500\nhttps://www.instagram.com/.../`}
          </Paper>
          <Typography variant="subtitle2" gutterBottom>
            Bu alanlar (yalnızca WhatsApp)
          </Typography>
          <FormGroup>
            {WHATSAPP_COLUMN_META.map((c) => (
              <FormControlLabel
                key={c.key}
                control={
                  <Checkbox
                    checked={whatsappCols[c.key]}
                    onChange={(e) => setWhatsappCols((prev) => ({ ...prev, [c.key]: e.target.checked }))}
                  />
                }
                label={c.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWhatsappOpen(false)}>İptal</Button>
          <Button variant="contained" color="success" onClick={() => void handleConfirmWhatsappCopy()} startIcon={<ChatIcon />}>
            WhatsApp metnini kopyala
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mergeOpen} onClose={() => !mergeSubmitting && setMergeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Influencer birleştir</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Aynı kişi için iki kayıt varsa: <strong>kaynak kullanıcı adı</strong> silinir; kampanyalar ve tespitler{' '}
            <strong>hedef kullanıcı adı</strong> kaydında birleşir. Hedef hesabın doğru olduğundan emin olun.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Kaynak kullanıcı adı (silinecek)"
              placeholder="eski_hesap"
              value={mergeSourceUsername}
              onChange={(e) => setMergeSourceUsername(e.target.value)}
              fullWidth
              disabled={mergeSubmitting}
              autoComplete="off"
            />
            <TextField
              label="Hedef kullanıcı adı (kalacak)"
              placeholder="yeni_hesap"
              value={mergeTargetUsername}
              onChange={(e) => setMergeTargetUsername(e.target.value)}
              fullWidth
              disabled={mergeSubmitting}
              autoComplete="off"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMergeOpen(false)} disabled={mergeSubmitting}>
            İptal
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={mergeSubmitting}
            onClick={() => void submitMerge()}
            startIcon={mergeSubmitting ? <CircularProgress size={18} color="inherit" /> : <MergeTypeIcon />}
          >
            Birleştir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={verifyConfirmOpen} onClose={() => !verifyRunning && setVerifyConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Toplu profil doğrulama</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>{selectedCount}</strong> kayıt, her HTTP isteğinde en fazla{' '}
            <strong>{BATCH_VERIFY_CHUNK}</strong> profil olacak şekilde ardışık isteklerle doğrulanır (proxy zaman
            aşımı / 524 riskini azaltır). Başarılı olanların ad, takipçi, bio ve profil fotoğrafı güncellenir. Toplam
            süre kayıt sayısına göre uzun olabilir. RapidAPI kotanıza dikkat edin.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVerifyConfirmOpen(false)} disabled={verifyRunning}>
            İptal
          </Button>
          <Button variant="contained" color="secondary" onClick={() => void runBatchVerify()} disabled={verifyRunning}>
            Başlat
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={verifyResultOpen}
        onClose={() => setVerifyResultOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Doğrulama sonucu</DialogTitle>
        <DialogContent dividers>
          {verifyResult ? (
            <Stack spacing={2}>
              <Typography variant="body2">
                Güncellenen: <strong>{verifyResult.updated.length}</strong> · Sorunlu:{' '}
                <strong>{verifyResult.failed.length}</strong>
                {verifyResult.username_hints.length > 0 ? (
                  <>
                    {' '}
                    · Kullanıcı adı farkı: <strong>{verifyResult.username_hints.length}</strong>
                  </>
                ) : null}
              </Typography>

              {verifyResult.networkFailedIds.length > 0 ? (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      disabled={verifyRunning}
                      onClick={() => void retryNetworkFailedVerify()}
                    >
                      Tekrar dene
                    </Button>
                  }
                >
                  {verifyResult.networkFailedIds.length} kayıt ağ kesintisi veya zaman aşımı nedeniyle bu turda
                  işlenemedi. Bağlantınızı kontrol edip «Tekrar dene» ile yalnızca bu listeyi yeniden gönderebilirsiniz.
                </Alert>
              ) : null}

              {verifyResult.username_hints.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    API farklı kullanıcı adı döndü (hesap yeniden adlandırılmış olabilir)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Kayıt ID</TableCell>
                          <TableCell>Veritabanı</TableCell>
                          <TableCell>API</TableCell>
                          <TableCell align="right">İşlem</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {verifyResult.username_hints.map((h) => (
                          <TableRow key={h.id}>
                            <TableCell>{h.id}</TableCell>
                            <TableCell>@{h.stored_username}</TableCell>
                            <TableCell>@{h.api_username}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={usernameEditSaving}
                                  onClick={() => void applySuggestedUsername(h.id, h.api_username)}
                                >
                                  Önerileni kaydet
                                </Button>
                                <Button size="small" onClick={() => openUsernameEdit(h.id, h.stored_username)}>
                                  Elle düzenle
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : null}

              {verifyResult.failed.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Ulaşılamayan veya hatalı profiller
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Kullanıcı adı</TableCell>
                          <TableCell>Hata</TableCell>
                          <TableCell align="right">İşlemler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {verifyResult.failed.map((f) => (
                          <TableRow key={`${f.id}-${f.instagram_username}`}>
                            <TableCell>{f.id}</TableCell>
                            <TableCell>{f.instagram_username ? `@${f.instagram_username}` : '—'}</TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                                {f.error}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Kullanıcı adını düzelt">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openUsernameEdit(f.id, f.instagram_username)}
                                  aria-label="düzenle"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Tekrar API ile dene">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => void retryVerifySingle(f.id)}
                                  aria-label="yeniden dene"
                                >
                                  <VerifiedUserIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Kaydı sil">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => void handleDeleteInfluencerFromVerify(f.id)}
                                  aria-label="sil"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : verifyResult.username_hints.length > 0 ? (
                <Alert severity="info">
                  API erişimi tamam; yukarıdaki kullanıcı adı farklarını inceleyin.
                </Alert>
              ) : (
                <Alert severity="success">Tüm seçilen profiller başarıyla güncellendi.</Alert>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVerifyResultOpen(false)} variant="contained">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={usernameEditOpen} onClose={() => !usernameEditSaving && setUsernameEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Kullanıcı adını düzenle</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Instagram kullanıcı adı"
            fullWidth
            value={usernameEditValue}
            onChange={(e) => setUsernameEditValue(e.target.value)}
            helperText="@ olmadan, yalnızca harf, rakam, nokta ve alt çizgi"
            disabled={usernameEditSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => !usernameEditSaving && setUsernameEditOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={() => void saveUsernameEdit()} disabled={usernameEditSaving}>
            {usernameEditSaving ? <CircularProgress size={22} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InfluencerDirectory;
